import { generateUUID } from '@/utils'
import BenzAMRRecorder from 'benz-amr-recorder'
import { useEffect, useRef, useState } from 'react'
import { useChatStore } from '../Chat/chatStore'
import { PauseSvg } from './PauseSvg'
import { AudioPlaySvg } from './PlaySvg'
import { RunningSvg } from './RunningSvg'

/**
 * AMR音频播放器组件
 *
 * 这是一个专门用于播放AMR格式音频文件的播放器组件，支持播放、暂停、自动切换等功能。
 * 使用BenzAMRRecorder库来处理AMR格式的音频文件，集成了全局音频播放状态管理。
 *
 * @param audioUrl - AMR音频文件的URL地址
 *
 * 使用场景：
 * 1. 聊天消息音频 - 聊天中的语音消息播放
 * 2. 语音留言 - 语音留言的播放功能
 * 3. 音频通知 - 系统音频通知的播放
 * 4. 语音备忘录 - 语音备忘录的播放
 * 5. 会议录音 - 会议录音文件的播放
 * 6. 语音转文字 - 语音文件的播放预览
 * 7. 音频教程 - 音频教程内容的播放
 * 8. 语音播报 - 系统语音播报功能
 *
 * 使用示例：
 * ```tsx
 * // 基础用法
 * <AmrPlayer audioUrl="https://example.com/audio.amr" />
 *
 * // 在聊天消息中使用
 * <div className="message-audio">
 *   <AmrPlayer audioUrl={message.audioUrl} />
 * </div>
 *
 * // 在列表中使用
 * {audioList.map(audio => (
 *   <div key={audio.id}>
 *     <AmrPlayer audioUrl={audio.url} />
 *   </div>
 * ))}
 *
 * // 条件渲染
 * {audioUrl && <AmrPlayer audioUrl={audioUrl} />}
 * ```
 */
const AmrPlayer = ({ audioUrl }) => {
  // 从聊天状态管理中获取音频播放相关的状态和方法
  const { tool_ids, tool_enable, audioPlayingId, setAudioPlayingId } = useChatStore()

  // AMR播放器实例的引用
  const amrPlayerRef = useRef<any>(null)

  // 当前音频的播放状态
  const [playing, setPlaying] = useState(false)

  // 为当前音频实例生成唯一ID，用于全局播放状态管理
  const audioId = useRef(generateUUID(16))
  /**
   * 监听全局音频播放状态变化
   * 当其他音频开始播放时，自动暂停当前音频
   */
  useEffect(() => {
    // 如果当前播放的音频不是当前实例，则暂停当前音频
    if (audioPlayingId !== audioId.current && amrPlayerRef.current) {
      amrPlayerRef.current.pause() // 暂停之前的音频
    }
  }, [audioPlayingId])

  /**
   * 初始化AMR播放器
   * 当audioUrl变化时，重新创建播放器实例并加载音频文件
   */
  useEffect(() => {
    if (!audioUrl) return

    // 创建新的AMR播放器实例
    amrPlayerRef.current = new BenzAMRRecorder()
    amrPlayerRef.current
      .initWithUrl(audioUrl)
      .then(() => {
        // 音频文件加载成功
        console.log('AMR音频文件加载成功')
      })
      .catch(err => {
        console.error('加载在线 AMR 文件失败:', err)
      })

    // 清理函数：组件卸载时销毁播放器实例
    return () => {
      if (amrPlayerRef.current) {
        try {
          amrPlayerRef.current.destroy()
        } catch (error) {
          console.error('销毁 AMR 播放器失败:', error)
        }
      }
    }
  }, [audioUrl])

  /**
   * 处理播放按钮点击事件
   * 开始播放音频并更新全局播放状态
   */
  const handlePlay = () => {
    if (amrPlayerRef.current && amrPlayerRef.current.isInit()) {
      setAudioPlayingId(audioId.current) // 设置当前音频为全局播放状态
      amrPlayerRef.current.play() // 开始播放音频
      setPlaying(true) // 更新本地播放状态
      amrPlayerRef.current.onEnded = () => setPlaying(false) // 设置播放结束回调
    } else {
      alert('音频尚未加载完成')
    }
  }

  /**
   * 处理暂停按钮点击事件
   * 暂停当前播放的音频并更新播放状态
   */
  const handlePause = () => {
    // 只有当前正在播放的音频才执行暂停操作
    if (audioPlayingId === audioId.current && amrPlayerRef.current && amrPlayerRef.current.isPlaying()) {
      setAudioPlayingId(null) // 清空全局播放状态
      amrPlayerRef.current.pause() // 暂停音频播放
      setPlaying(false) // 更新本地播放状态
    }
  }

  return (
    <span>
      {/* 未播放状态：显示播放按钮 */}
      {audioPlayingId !== audioId.current && (
        <div className="cursor-pointer" onClick={handlePlay}>
          {AudioPlaySvg}
        </div>
      )}

      {/* 播放状态：显示动态播放图标和暂停按钮 */}
      {audioPlayingId === audioId.current && (
        // 注释掉的简单暂停按钮实现
        // <span className="cursor-pointer" onClick={handlePause}>
        //   {PauseSvg}
        // </span>

        <div className="group relative">
          {/* 默认显示动态播放图标 */}
          <div className="audio-running-div block group-hover:hidden">{RunningSvg}</div>
          {/* 悬停时显示暂停按钮 */}
          <div
            className="hidden cursor-pointer group-hover:block"
            onClick={() => {
              handlePause()
            }}
          >
            {PauseSvg}
          </div>
        </div>
      )}
    </span>
  )
}

export default AmrPlayer
